import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useRefresh } from '../../contexts/RefreshContext';
import useClickOutside from '../../hooks/useClickOutside';

const Header = ({ onToggleSidebar }) => {
  const { user, logout } = useAuth();
  const { triggerRefresh, hasRefresh } = useRefresh();
  const location = useLocation();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const isDashboard = location.pathname === '/dashboard';

  const handleRefresh = async () => {
    setRefreshing(true);
    await triggerRefresh();
    setRefreshing(false);
  };

  const handleLogout = () => {
    const redirectPath = logout();
    window.location.href = redirectPath;
  };

  const toggleProfileMenu = () => {
    setShowProfileMenu(!showProfileMenu);
  };

  const closeProfileMenu = () => {
    setShowProfileMenu(false);
  };

  const profileRef = useClickOutside(closeProfileMenu);

  return (
    <header className="bg-gradient-to-r from-[#009ef7] to-[#0077c2] shadow-lg">
      <div className="flex items-center justify-between px-6 py-2">
        {/* Mobile Menu Button */}
        <button
          onClick={onToggleSidebar}
          className="lg:hidden flex items-center justify-center w-8 h-8 bg-white/10 hover:bg-white/20 rounded-lg transition-all backdrop-blur-sm border border-white/20"
          title="Toggle Menu"
        >
          <i className="bi bi-list text-white text-xl"></i>
        </button>

        {/* Spacer for desktop */}
        <div className="hidden lg:block"></div>

        <div className="flex items-center space-x-2">
          {/* Refresh Button - Only on Dashboard */}
          {isDashboard && hasRefresh && (
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center space-x-1 px-3 py-1 bg-white/10 hover:bg-white/20 rounded-lg transition-all backdrop-blur-sm border border-white/20 disabled:opacity-50"
              title="Refresh Dashboard"
            >
              <i className={`bi bi-arrow-clockwise text-white text-sm ${refreshing ? 'animate-spin' : ''}`}></i>
              <span className="text-xs text-white font-medium hidden md:inline">Refresh</span>
            </button>
          )}

          {/* Profile Menu */}
          <div className="relative" ref={profileRef}>
            <button 
              onClick={toggleProfileMenu}
              className="flex items-center space-x-2 px-2 py-1 bg-white/10 hover:bg-white/20 rounded-lg transition-all backdrop-blur-sm border border-white/20"
            >
              <div className="relative">
                <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                  <i className="bi bi-person-fill text-sm" style={{ color: '#009ef7' }}></i>
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-green-400 border border-white rounded-full"></div>
              </div>
              <div className="hidden md:block text-left">
                <div className="text-xs font-semibold text-white leading-none">
                  {user?.first_name || 'Admin'} {user?.last_name || 'User'}
                </div>
                <div className="text-[10px] text-blue-100 font-medium capitalize mt-0.5">
                  {user?.role || 'Administrator'}
                </div>
              </div>
              <i className={`bi bi-chevron-down text-white/80 text-[10px] transition-transform ${showProfileMenu ? 'rotate-180' : ''}`}></i>
            </button>

            {/* Profile Dropdown */}
            {showProfileMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
                <div className="p-0">
                  <button 
                    onClick={handleLogout} 
                    className="flex items-center w-full px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-all group"
                  >
                    <div className="w-8 h-8 bg-red-100 group-hover:bg-red-200 rounded-lg flex items-center justify-center mr-3 transition-colors">
                      <i className="bi bi-box-arrow-right text-red-600"></i>
                    </div>
                    <span className="font-semibold">Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;